import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import authService from '../../services/authService';

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setErrorMessage('Le prénom est requis.');
      return false;
    }
    if (!formData.lastName.trim()) {
      setErrorMessage('Le nom est requis.');
      return false;
    }
    if (!formData.email.trim()) {
      setErrorMessage('L\'email est requis.');
      return false;
    }
    if (formData.password.length < 8) {
      setErrorMessage('Le mot de passe doit contenir au moins 8 caractères.');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Les mots de passe ne correspondent pas.');
      return false;
    }
    if (!acceptTerms) {
      setErrorMessage('Vous devez accepter les conditions d\'utilisation.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowAlert(false);

    if (!validateForm()) {
      setShowAlert(true);
      return;
    }

    setIsLoading(true);

    try {
      const data = await authService.registerCoach({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
      });

      // Inscription réussie - connecter l'utilisateur
      login(data.token);

      // Rediriger vers le checkout (non abonné par défaut)
      navigate('/coach/checkout');

    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);

      if (error.message.includes('existe') || error.message.includes('already')) {
        setErrorMessage('Cet email est déjà utilisé. Essayez de vous connecter.');
      } else {
        setErrorMessage(error.message || 'Erreur lors de l\'inscription. Veuillez réessayer.');
      }
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Yoco - Inscription Coach</title>
        <meta name="keywords" content="coaching, sport, nutrition, inscription, coach" />
      </Helmet>

      <Container sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', py: 4 }} maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            px: { xs: 2, sm: 4 },
            borderRadius: 2,
            border: '1px solid #DDD',
            backgroundColor: 'white',
            width: '100%',
          }}
        >
          {/* Retour */}
          <Stack direction="row" alignItems="center" sx={{ width: '100%', mb: 2 }}>
            <IconButton component={Link} to="/" size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              Retour à la connexion
            </Typography>
          </Stack>

          <Typography variant="h2" sx={{ mb: 1 }}>
            Créer un compte Coach
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Rejoignez Yoco et commencez à accompagner vos clients dans leur parcours nutritionnel.
          </Typography>

          {/* Message d'erreur */}
          <Collapse in={showAlert} sx={{ width: '100%' }}>
            <Alert
              severity="error"
              onClose={() => setShowAlert(false)}
              sx={{ mb: 2 }}
            >
              {errorMessage}
            </Alert>
          </Collapse>

          {/* Formulaire */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                required
                fullWidth
                id="firstName"
                label="Prénom"
                name="firstName"
                autoComplete="given-name"
                autoFocus
                value={formData.firstName}
                onChange={handleChange}
                disabled={isLoading}
              />
              <TextField
                required
                fullWidth
                id="lastName"
                label="Nom"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Stack>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />

            <TextField
              margin="normal"
              fullWidth
              id="phone"
              label="Téléphone (optionnel)"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={formData.phone}
              onChange={handleChange}
              disabled={isLoading}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              helperText="Minimum 8 caractères"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirmer le mot de passe"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
              error={formData.confirmPassword && formData.password !== formData.confirmPassword}
              helperText={
                formData.confirmPassword && formData.password !== formData.confirmPassword
                  ? 'Les mots de passe ne correspondent pas'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label={
                <Typography variant="body2">
                  J'accepte les{' '}
                  <Link to="/terms" style={{ color: 'primary' }}>
                    conditions d'utilisation
                  </Link>{' '}
                  et la{' '}
                  <Link to="/privacy-policy" style={{ color: 'primary' }}>
                    politique de confidentialité
                  </Link>
                </Typography>
              }
              sx={{ mt: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading || !acceptTerms}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Créer mon compte'
              )}
            </Button>

            <Stack direction="row" justifyContent="center">
              <Typography variant="body2" color="text.secondary">
                Déjà inscrit ?{' '}
                <Link to="/" style={{ color: 'inherit', fontWeight: 600 }}>
                  Se connecter
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Register;
